import { Card, CardContent } from '../ui/card';
import { SectionLabel, PageDescription } from '../layout/Typography';
import { cn } from '../../lib/utils';

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  description?: React.ReactNode;
  valueClassName?: string;
  align?: 'left' | 'center';
  className?: string;
  selected?: boolean;
  onClick?: () => void;
}

export function StatCard({
  label,
  value,
  icon,
  description,
  valueClassName,
  align = 'left',
  className,
  selected,
  onClick,
}: StatCardProps) {
  const interactive = !!onClick;
  const handleKeyDown = interactive
    ? (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick!();
        }
      }
    : undefined;

  if (align === 'center') {
    return (
      <Card
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-pressed={interactive ? selected || undefined : undefined}
        aria-label={interactive ? label : undefined}
        data-testid="stat-card"
        data-align="center"
        data-selected={selected ? 'true' : undefined}
        className={cn(
          'relative overflow-hidden p-4 text-center',
          interactive && 'cursor-pointer transition-colors hover:bg-muted/50',
          selected && 'ring-2 ring-primary',
          className,
        )}
        onClick={onClick}
        onKeyDown={handleKeyDown}
      >
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent"
        />
        <p
          data-testid="stat-card-value"
          className={cn('text-3xl font-light tracking-tighter tabular-nums', valueClassName)}
        >
          {value}
        </p>
        <SectionLabel as="span" className="mt-0.5">
          {label}
        </SectionLabel>
        {description && <PageDescription className="text-xs">{description}</PageDescription>}
      </Card>
    );
  }

  return (
    <Card
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-pressed={interactive ? selected || undefined : undefined}
      aria-label={interactive ? label : undefined}
      data-testid="stat-card"
      data-align="left"
      data-selected={selected ? 'true' : undefined}
      className={cn(
        'relative overflow-hidden',
        interactive && 'cursor-pointer transition-colors hover:bg-muted/50',
        selected && 'ring-2 ring-primary',
        className,
      )}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent"
      />
      <CardContent className="flex items-center justify-between px-4 py-3">
        <div>
          <SectionLabel as="span">{label}</SectionLabel>
          <p
            data-testid="stat-card-value"
            className={cn(
              'mt-0.5 text-3xl font-light tracking-tighter tabular-nums',
              valueClassName,
            )}
          >
            {value}
          </p>
          {description && <PageDescription className="text-xs">{description}</PageDescription>}
        </div>
        {icon && <div className="text-muted-foreground/40">{icon}</div>}
      </CardContent>
    </Card>
  );
}
